import { Injectable } from '@angular/core';
import { TemporaryDocument } from '@via/models';

@Injectable({
  providedIn: 'root',
})
export class IndexedDBService {
  private _dbName = 'TemporaryDocumentStorage';
  private _storeName = 'documentStore';
  private _db?: IDBDatabase;

  constructor() {
    this.openDatabase();
  }

  private openDatabase(): Promise<IDBDatabase> {
    if (this._db) {
      return Promise.resolve(this._db);
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this._dbName, 1);

      request.onerror = () => {
        reject(new Error('Error opening IndexedDB'));
      };

      request.onsuccess = () => {
        this._db = request.result;
        resolve(request.result);
      };

      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(this._storeName)) {
          db.createObjectStore(this._storeName);
        }
      };
    });
  }

  async storeDocument(
    htmlString: string,
    file: File | undefined,
    ttlSeconds: number = 3600,
    type: string = 'temporary'
  ): Promise<string> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this._storeName], 'readwrite');
      const objectStore = transaction.objectStore(this._storeName);
      const key = crypto.randomUUID();
      const expiry = Date.now() + ttlSeconds * 1000;
      const request = objectStore.put(
        { htmlContent: htmlString, file, type, expiry },
        key
      );
      request.onsuccess = () => {
        resolve(key); // The auto-generated ID
      };

      request.onerror = () => {
        reject(new Error('Error storing HTML'));
      };
    });
  }

  async fetchDocument(key: string): Promise<TemporaryDocument | undefined> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this._storeName]);
      const objectStore = transaction.objectStore(this._storeName);
      const request = objectStore.get(key);

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = () => {
        reject(new Error('Error fetching HTML'));
      };
    });
  }

  async removeDocument(key: string): Promise<void> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([this._storeName], 'readwrite');
      const objectStore = transaction.objectStore(this._storeName);
      const request = objectStore.delete(key);

      request.onsuccess = () => {
        resolve();
      };

      request.onerror = () => {
        reject(new Error('Error removing HTML'));
      };
    });
  }

  async removeExpiredDocuments() {
    const db = await this.openDatabase();
    const transaction = db.transaction(this._storeName, 'readwrite');
    const store = transaction.objectStore(this._storeName);

    const cursor = store.openCursor();
    const now = Date.now();

    cursor.onsuccess = (event) => {
      const cursor = (event.target as any).result;
      if (cursor) {
        const data = cursor.value;
        if (data.expiry && now > data.expiry) {
          store.delete(cursor.key);
        }
        cursor.continue();
      }
    };
  }
}
